import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import './mass-printing.scss';
import { newToast } from '../../components';
import DataGrid, { Column, Paging, Scrolling, Selection, Sorting, KeyboardNavigation } from 'devextreme-react/data-grid';
import { useAppSelector, useAppDispatch, selectModuleLocation } from '../../contexts/hooks';
import { selectResultsPermissions, selectModuleEmail, selectModuleWhatsApp } from '../../contexts/selectors';
import { shallowEqual } from 'react-redux';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Button, LoadIndicator, LoadPanel, Tooltip, ContextMenu } from 'devextreme-react';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import Form, { Item as FormItem, Label } from 'devextreme-react/form';
import DropDownBox from 'devextreme-react/drop-down-box';
import { ScrollView } from 'devextreme-react';
import { filter, resButtonRefresh, resButtonsPrint, resButtonsMail, whatsapp } from '../../images/icons';
import { ConvertLocalDate, ConvertDataOrder, ConvertDataPatient } from '../../utils/functions';
import { useApiOrders } from '../../api/api.orders';
import { useScreenSize } from '../../utils/media-query';
import { FilteredOrders, DinamicFiltersOrders, ResultEstadoResult, SendResultByEmailMasiveOrder } from '../../types/types.orders';
import { SystemFields } from '../../types/types.config';
import { CustomFormControl } from '../../components/utils/form-render/FormRender';
import { useFilterStudiesDropDown } from '../../components/utils/filter-popup/hooks/useFilterStudiesDropDown';
import { FilterStudiesDataGrid } from '../../components/utils/filter-popup/components/FilterStudiesDataGrid';
import { ModalReportResults } from '../../components/library/reporting/ModalReportResults';
import { SendMailMasive } from './modals/SendMailMasive';
import { RightPanelControlled } from '../../components/utils/right-panel-controlled/RighPanel';
import { OrderInfo, PatientInfo } from '../../components/library/order-patient-info';

const Index = () => {
  //#region VARIABLES Y ESTADOS
  const { GetOrdersByFilterForMasive, getHeaderOrderById, findOrder } = useApiOrders();
  const gridRef = useRef<DataGrid>(null);
  const frmRef = useRef<Form>(null);
  const refDropDownStudies = useRef<DropDownBox>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [modalFiltersVisible, setModalFiltersVisible] = useState<boolean>(true); // Abre modal de filtros al entrar
  const [appliedFiltersText, setAppliedFiltersText] = useState<string>('');
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [gridData, setGridData] = useState<any[]>([]);
  const [modalPrintResult, setModalPrintResult] = useState<{ visible: boolean; orders?: number[]; studies?: number[]; areas?: number[] }>({ visible: false });
  const [modalSendMail, setModalSendMail] = useState<{ visible: boolean; dataOrder?: SendResultByEmailMasiveOrder[]; studies?: number[]; areas?: number[] }>({ visible: false });

  const dispatch = useAppDispatch();
  const { isXSmall, isMedium, isLarge } = useScreenSize();

  // Datos de configuración
  const LstAreas = useAppSelector((x) => x.configuration.Data?.Areas, shallowEqual) || [];
  const LstEstudios = useAppSelector((x) => x.configuration.Data?.Estudios, shallowEqual);
  const LstServicios = useAppSelector((x) => x.configuration.Data?.ComboBox, shallowEqual)?.filter((x) => x.Name === 'SysA_Servicios')[0]?.Data;
  const LstSedes = useAppSelector((x) => x.configuration.Data?.ComboBox, shallowEqual)?.filter((x) => x.Name === 'SysI_Sedes')[0]?.Data;
  const AllComboBoxData = useAppSelector((x) => x.configuration.Data?.ComboBox, shallowEqual) || [];
  const Prm_Ord_ViewOtherLocation = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_ViewOtherLocation, shallowEqual);
  const ModuleLocation = useAppSelector(selectModuleLocation);
  const modeEditor = useAppSelector((x) => x.configuration.IsEditing, shallowEqual);

  // Permisos
  const permissions = useAppSelector(selectResultsPermissions);
  const ModuleEmail = useAppSelector(selectModuleEmail);
  const ModuleWhatsApp = useAppSelector(selectModuleWhatsApp);
  const Prm_ResImprimir = permissions.canPrintResults;
  const Prm_Res_Mail = permissions.canSendEmail;
  const Prm_Res_WhatsApp = permissions.canSendWhatsApp;

  // Campos dinámicos de órdenes
  const dynamicOrderFields =
    useAppSelector((x) => x.configuration.Data?.SystemFields, shallowEqual)
      ?.filter((x) => x.Dfc_Tabla === 'Ordenes' && x.Dfc_Estado === true && x.Dfc_Visible === true && x.Dfc_Dinamico === true)
      .sort((a, b) => a.Dfc_Orden - b.Dfc_Orden) || [];

  // Estado para valores de campos dinámicos
  const [dynamicFieldValues, setDynamicFieldValues] = useState<{ [key: string]: any }>({});

  // Estado para estudios seleccionados
  const [selectedStudios, setSelectedStudios] = useState<number[]>([]);

  // Ref para almacenar los filtros aplicados (persisten después de cerrar el modal)
  const appliedFiltersRef = useRef<FilteredOrders | null>(null);

  // Ref para almacenar las filas seleccionadas
  const selectedRowsRef = useRef<any[]>([]);
  const [hasSelection, setHasSelection] = useState<boolean>(false);

  // Estado para el panel derecho de información de orden
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [currentPatient, setCurrentPatient] = useState<any>(null);
  const [loadingPanel, setLoadingPanel] = useState<boolean>(false);
  const [loadingFindOrder, setLoadingFindOrder] = useState<boolean>(false);

  // Datos del formulario de filtros (useRef para que no se reinicie en cada render)
  const FieldsToFiltersRef = useRef<FilteredOrders>({
    Fechas: [new Date().toISOString(), new Date().toISOString()],
  });
  //#endregion

  //#region DYNAMIC FIELDS HANDLERS
  const handleDynamicFieldChange = useCallback(
    (fieldName: string) => (value: any) => {
      setDynamicFieldValues((prev) => ({
        ...prev,
        [fieldName]: value,
      }));
    },
    []
  );

  const mapDynamicFieldsToBackend = useCallback((): DinamicFiltersOrders[] => {
    const camposDinamicos: DinamicFiltersOrders[] = [];

    Object.entries(dynamicFieldValues).forEach(([fieldName, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const values = Array.isArray(value) ? value : [value];
        if (values.length > 0) {
          const fieldConfig = dynamicOrderFields.find((f) => f.Dfc_Equivalencia === fieldName);
          const label = fieldConfig?.Dfc_Label || fieldName;

          let displayValues: string[] | undefined;
          const fieldType = fieldConfig?.TipoCampo?.Tcm_Equivalencia;

          if (fieldConfig && (fieldType === 'dxSelectBox' || fieldType === 'dxLookup') && fieldConfig.TablasComboBox) {
            const comboBoxName = fieldConfig.TablasComboBox.Tbc_NombreTabla;
            const displayExpr = fieldConfig.TablasComboBox.Tbc_CampoVisible;
            const valueExpr = fieldConfig.TablasComboBox.Tbc_CampoId;
            const comboData = AllComboBoxData.find((cb) => cb.Name === comboBoxName)?.Data || [];

            displayValues = values.map((v) => {
              const item = comboData.find((d) => d[valueExpr] === v);
              return item ? item[displayExpr] : String(v);
            });
          }

          camposDinamicos.push({
            FieldName: fieldName,
            Label: label,
            Values: values,
            DisplayValues: displayValues,
          });
        }
      }
    });

    return camposDinamicos;
  }, [dynamicFieldValues, dynamicOrderFields, AllComboBoxData]);
  //#endregion

  //#region STUDIES DROPDOWN
  const handleStudiosChange = useCallback((estudiosIds: number[]) => {
    setSelectedStudios(estudiosIds);
  }, []);

  const filterStudiesDropDown = useFilterStudiesDropDown(handleStudiosChange);
  //#endregion

  //#region FETCH DATA
  const fetchData = useCallback(
    async (filters: FilteredOrders) => {
      setLoading(true);
      try {
        const res = await GetOrdersByFilterForMasive(filters);
        const dataArray = Array.isArray(res) ? res : [];
        setTotalRecords(dataArray.length);
        setGridData(dataArray);
      } catch {
        console.error('Error de carga de datos');
      } finally {
        setLoading(false);
      }
    },
    [GetOrdersByFilterForMasive]
  );
  //#endregion

  //#region EFFECTS
  // Restaurar estudios seleccionados cuando se abre el modal
  useEffect(() => {
    if (modalFiltersVisible && appliedFiltersRef.current) {
      // Restaurar estudios en el dropdown
      if (appliedFiltersRef.current.Estudios && appliedFiltersRef.current.Estudios.length > 0) {
        setSelectedStudios(appliedFiltersRef.current.Estudios);
      }
    }
  }, [modalFiltersVisible]);
  //#endregion

  //#region HANDLERS
  const handleRefresh = useCallback(() => {
    if (appliedFiltersRef.current) {
      fetchData(appliedFiltersRef.current);
    }
  }, [fetchData]);

  const handleModalShowFilter = useCallback(() => {
    setModalFiltersVisible(!modalFiltersVisible);
  }, [modalFiltersVisible]);

  const handleConfirm = useCallback(() => {
    if (frmRef.current?.instance) {
      const fechas = frmRef.current.instance.getEditor('Fechas')?.option('value');
      const ordenDesde = frmRef.current.instance.getEditor('OrdenDesde')?.option('value');
      const ordenHasta = frmRef.current.instance.getEditor('OrdenHasta')?.option('value');
      const paciente = frmRef.current.instance.getEditor('Paciente')?.option('value');
      const areas = frmRef.current.instance.getEditor('Areas')?.option('value');
      const servicios = frmRef.current.instance.getEditor('Servicios')?.option('value');
      const sedes = frmRef.current.instance.getEditor('Sedes')?.option('value');
      const remitido = frmRef.current.instance.getEditor('Remitido')?.option('value');

      // Rango de orden: desde - hasta. Si "hasta" está vacío se usa el valor de "desde".
      const ordenDesdeVal = (ordenDesde || '').trim();
      const ordenHastaVal = (ordenHasta || '').trim();

      const hasFechas = fechas && Array.isArray(fechas) && fechas.length === 2 && fechas[0] && fechas[1];
      const hasOrden = ordenDesdeVal !== '' || ordenHastaVal !== '';
      const hasPaciente = paciente && paciente.trim() !== '';

      if (!hasFechas && !hasOrden && !hasPaciente) {
        const fechasEditor = frmRef.current.instance.getEditor('Fechas');
        const ordenDesdeEditor = frmRef.current.instance.getEditor('OrdenDesde');
        const ordenHastaEditor = frmRef.current.instance.getEditor('OrdenHasta');
        const pacienteEditor = frmRef.current.instance.getEditor('Paciente');

        if (fechasEditor) fechasEditor.option('isValid', false);
        if (ordenDesdeEditor) ordenDesdeEditor.option('isValid', false);
        if (ordenHastaEditor) ordenHastaEditor.option('isValid', false);
        if (pacienteEditor) pacienteEditor.option('isValid', false);

        return;
      }

      // El backend espera un array [desde, hasta]; ambos extremos se completan entre sí.
      const ordenRango = hasOrden ? [ordenDesdeVal || ordenHastaVal, ordenHastaVal || ordenDesdeVal] : undefined;

      // Guardar los filtros en el ref ANTES de cerrar el modal
      const fechasConvertidas = hasFechas ? fechas.map((fecha: Date) => ConvertLocalDate(new Date(fecha))) : [];
      const camposDinamicos = mapDynamicFieldsToBackend();

      appliedFiltersRef.current = {
        Fechas: fechasConvertidas,
        Orden: ordenRango,
        Paciente: paciente || '',
        Areas: areas || [],
        Estudios: selectedStudios || [],
        Servicios: servicios || [],
        Sedes: sedes || [],
        Estados: [ResultEstadoResult.Validado, ResultEstadoResult.Impreso, ResultEstadoResult.OtrasFuentes, ResultEstadoResult.ValidadoBlanco],
        Remitido: remitido !== undefined ? remitido : undefined,
        Redirect: false,
        CamposDinamicos: camposDinamicos.length > 0 ? camposDinamicos : undefined,
      };

      setModalFiltersVisible(false);

      // Construir texto de filtros aplicados
      const parts: string[] = [];
      if (hasFechas) {
        const moment = require('moment');
        parts.push(`${moment(fechas[0]).format('DD/MM/YYYY')} - ${moment(fechas[1]).format('DD/MM/YYYY')}`);
      }
      if (hasOrden) parts.push(ordenDesdeVal === ordenHastaVal || !ordenHastaVal ? `Orden: ${ordenRango?.[0]}` : `Orden: ${ordenRango?.[0]} - ${ordenRango?.[1]}`);
      if (hasPaciente) parts.push(`Doc: ${paciente}`);

      if (areas && areas.length > 0) {
        const areaNames = areas.map((id: number) => LstAreas.find((a) => a.Aa_Id === id)?.Aa_Nombre).filter(Boolean);
        parts.push(`Áreas: ${areaNames.join(', ')}`);
      }

      setAppliedFiltersText(parts.join(' | '));

      // Ejecutar búsqueda
      fetchData(appliedFiltersRef.current);
    }
  }, [LstAreas, selectedStudios, mapDynamicFieldsToBackend, fetchData]);

  const handleCancel = useCallback(() => {
    setModalFiltersVisible(false);
  }, []);

  const handleClearFilters = useCallback(() => {
    const today = new Date();
    const fechasEditor = frmRef.current?.instance.getEditor('Fechas');
    if (fechasEditor) {
      fechasEditor.option('value', [today, today]);
    }

    const fieldsToReset = ['OrdenDesde', 'OrdenHasta', 'Paciente', 'Areas', 'Servicios', 'Sedes', 'Remitido'];
    fieldsToReset.forEach((field) => {
      const editor = frmRef.current?.instance.getEditor(field);
      if (editor) {
        editor.option('value', null);
      }
    });

    setDynamicFieldValues({});
    setSelectedStudios([]);
    filterStudiesDropDown.syncStudiesSelection({ value: [] });
  }, [filterStudiesDropDown]);

  // Handler para selección de filas
  const handleSelectionChanged = useCallback((e: any) => {
    selectedRowsRef.current = e.selectedRowsData || [];
    setHasSelection((e.selectedRowsData || []).length > 0);
  }, []);

  // Handlers para el panel derecho de información de orden
  const handleOrderClick = useCallback(
    async (orderId: number) => {
      if (!orderId) return;
      setIsPanelOpen(true);
      setLoadingPanel(true);
      try {
        const res = await getHeaderOrderById(orderId);
        if (res) {
          const { Orden, Paciente } = res;
          setCurrentOrder(ConvertDataOrder(Orden));
          setCurrentPatient(ConvertDataPatient(Paciente));
        } else {
          setCurrentOrder(null);
          setCurrentPatient(null);
          newToast('WARNING', 'No se encontró información de la orden', 3);
        }
      } catch {
        newToast('ERROR', 'Error al cargar los datos de la orden', 3);
        setCurrentOrder(null);
        setCurrentPatient(null);
      } finally {
        setLoadingPanel(false);
      }
    },
    [getHeaderOrderById]
  );

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setCurrentOrder(null);
    setCurrentPatient(null);
  }, []);

  const handleClickViewOrder = useCallback(() => {
    if (currentOrder) {
      findOrder(undefined, currentOrder.Ord_Id, setLoadingFindOrder, true);
    }
  }, [currentOrder, findOrder]);

  const renderOrderCell = useCallback(
    (cellData: any) => {
      const ordId: number = cellData.data?.Ord_Id;
      const ordNumero = cellData.data?.Ord_Numero ?? ordId;
      return (
        <div className='mass-printing-order-cell' onClick={() => handleOrderClick(ordId)}>
          {ordNumero}
        </div>
      );
    },
    [handleOrderClick]
  );

  // Colorear filas según Se_Color (color del servicio)
  const rowPrepared = useCallback((e: any) => {
    if (e.rowType === 'data') {
      const color = e.data.Se_Color;
      if (color && color.trim() !== '') {
        e.rowElement.style.color = color;
      }
    }
  }, []);

  // Handler para imprimir resultados
  const handlePrintResults = useCallback(() => {
    const selected = selectedRowsRef.current;
    if (!selected || selected.length === 0) {
      newToast('INFO', 'Debe seleccionar al menos una orden para imprimir', 4);
      return;
    }
    if (!Prm_ResImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir resultados', 5);
      return;
    }

    const orderIds: number[] = selected.map((row: any) => row.Ord_Id);
    const filterStudies = appliedFiltersRef.current?.Estudios;
    const filterAreas = appliedFiltersRef.current?.Areas;

    setModalPrintResult({
      visible: true,
      orders: orderIds,
      studies: filterStudies && filterStudies.length > 0 ? filterStudies : undefined,
      areas: filterAreas && filterAreas.length > 0 ? filterAreas : undefined,
    });
  }, [Prm_ResImprimir]);

  // Handler para enviar por correo
  const handleSendMail = useCallback(() => {
    const selected = selectedRowsRef.current;
    if (!selected || selected.length === 0) {
      newToast('INFO', 'Debe seleccionar al menos una orden para enviar por correo', 4);
      return;
    }
    if (!Prm_Res_Mail) {
      newToast('INFO', 'No cuenta con los permisos para enviar resultados por correo', 5);
      return;
    }

    const dataOrder: SendResultByEmailMasiveOrder[] = selected.map((row: any) => ({
      IdOrder: row.Ord_Id,
      OrderNumber: row.Ord_Numero || '',
      Patient: [row.Pc_Nombre, row.Pc_Apellido1, row.Pc_Apellido2].filter(Boolean).join(' '),
      Document: row.Pc_Identificacion || '',
    }));
    const filterStudies = appliedFiltersRef.current?.Estudios;
    const filterAreas = appliedFiltersRef.current?.Areas;

    setModalSendMail({
      visible: true,
      dataOrder,
      studies: filterStudies && filterStudies.length > 0 ? filterStudies : undefined,
      areas: filterAreas && filterAreas.length > 0 ? filterAreas : undefined,
    });
  }, [Prm_Res_Mail]);

  // // Handler para enviar por WhatsApp (TODO: implementar lógica)
  // const handleSendWhatsApp = useCallback(() => {
  //   const selected = selectedRowsRef.current;
  //   if (!selected || selected.length === 0) {
  //     newToast('INFO', 'Debe seleccionar al menos una orden para enviar por WhatsApp', 4);
  //     return;
  //   }
  //   if (!Prm_Res_WhatsApp) {
  //     newToast('INFO', 'No cuenta con los permisos para enviar resultados por WhatsApp', 5);
  //     return;
  //   }
  //   console.log('Mass Printing - Enviar WhatsApp:', selected);
  //   newToast('INFO', `Enviando ${selected.length} orden(es) por WhatsApp...`, 3);
  // }, [Prm_Res_WhatsApp]);
  //#endregion

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper-contact-list'>
        <div className='view-wrapper-mass-printing' style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <div className='toolbar-mass-printing'>
            <Toolbar>
              <Item location='before' visible={isLarge || isMedium}>
                <div className='grid-header'>Impresión Masiva</div>
              </Item>
              {appliedFiltersText && (
                <Item location='before' locateInMenu='auto'>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0 8px' }}>
                    <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '500px' }}>{appliedFiltersText}</span>
                  </div>
                </Item>
              )}

              <Item location='after' locateInMenu='auto'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px', minWidth: 'fit-content' }}>
                  <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
                    Registros: <strong>{totalRecords}</strong>
                  </span>
                </div>
              </Item>
              <Item location='after' locateInMenu='auto'>
                <div className='separator' />
              </Item>
              <Item location='after' locateInMenu='auto' visible={Prm_ResImprimir} showText='inMenu' widget='dxButton'>
                <Button id='ImprimirResultMP' icon={resButtonsPrint} text='Imprimir resultados' stylingMode='text' onClick={handlePrintResults} disabled={!hasSelection} />
                <Tooltip target={'#ImprimirResultMP'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Imprimir resultados de las órdenes seleccionadas
                </Tooltip>
              </Item>
              <Item location='after' visible={ModuleEmail && Prm_Res_Mail} locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='EnviarResultMailMP' icon={resButtonsMail} text='Enviar resultados por correo' stylingMode='text' onClick={handleSendMail} disabled={!hasSelection} />
                <Tooltip target={'#EnviarResultMailMP'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Enviar resultados por correo
                </Tooltip>
              </Item>
              {/* <Item location='after' visible={ModuleWhatsApp && Prm_Res_WhatsApp} locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='EnviarResultWhatsAppMP' icon={whatsapp} text='Enviar resultados por WhatsApp' stylingMode='text' onClick={handleSendWhatsApp} disabled={!hasSelection} />
                <Tooltip target={'#EnviarResultWhatsAppMP'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Enviar resultados por WhatsApp
                </Tooltip>
              </Item> */}
              <Item location='after' locateInMenu='auto'>
                <div className='separator' />
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Tooltip target='.filter-button-mp' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                  Filtrar órdenes
                </Tooltip>
                <Button className='filter-button-mp' icon={filter} text='Filtrar' stylingMode='text' onClick={handleModalShowFilter} />
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='RefrescarMasivoPrint' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleRefresh} />
                <Tooltip target={'#RefrescarMasivoPrint'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Refrescar datos
                </Tooltip>
              </Item>
            </Toolbar>
          </div>

          <div id='gridContainer' style={{ flex: 1, overflow: 'auto', paddingBottom: '40px' }}>
            <DataGrid hoverStateEnabled className='gridResult' height={'100%'} dataSource={gridData} ref={gridRef} width={'100%'} keyExpr='Ord_Id' repaintChangesOnly={false} highlightChanges={false} rowAlternationEnabled={false} showColumnLines={true} cacheEnabled={false} onSelectionChanged={handleSelectionChanged} onRowPrepared={rowPrepared}>
              <Selection mode='multiple' selectAllMode='page' showCheckBoxesMode='always' />
              <Scrolling mode='standard' showScrollbar='always' useNative={true} scrollByContent={true} />
              <Sorting mode='multiple' />
              <KeyboardNavigation editOnKeyPress={false} enterKeyAction='moveFocus' enterKeyDirection='row' />
              <Paging enabled={false} />
              <Column dataField='Ord_Numero' caption='ORDEN' width={100} cellRender={renderOrderCell} />
              <Column dataField='Column1' caption='FECHA' dataType='date' format='dd/MM/yyyy' width={120} />
              <Column dataField='Pc_Identificacion' caption='DOCUMENTO' width={130} />
              <Column dataField='Pc_Nombre' caption='PACIENTE' calculateCellValue={(rowData) => [rowData.Pc_Nombre, rowData.Pc_Apellido1, rowData.Pc_Apellido2].filter(Boolean).join(' ')} />
              <Column dataField='Se_Nombre' caption='SERVICIO' width={180} />
            </DataGrid>
          </div>
        </div>

        {/* Modal de Filtros */}
        <Popup title='Filtrar órdenes para impresión' visible={modalFiltersVisible} showCloseButton={true} onHiding={handleModalShowFilter} fullScreen={isXSmall} width={isXSmall ? 360 : 600} wrapperAttr={{ class: 'form-popup' }} height={isXSmall ? '100%' : 'auto'} maxHeight={isXSmall ? '100%' : '80vh'}>
          <ScrollView height='100%' showScrollbar='onScroll'>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}>
              <Form ref={frmRef} formData={FieldsToFiltersRef.current} colCount={2}>
                <FormItem dataField='Fechas' colSpan={2} editorType='dxDateRangeBox' editorOptions={{ displayFormat: 'shortdate', showClearButton: true }}>
                  <Label text='Fecha' />
                </FormItem>
                <FormItem dataField='OrdenDesde' colSpan={1}>
                  <Label text='Orden desde' />
                </FormItem>
                <FormItem
                  dataField='OrdenHasta'
                  colSpan={1}
                  editorOptions={{
                    onFocusIn: (e: any) => {
                      const actual = e.component.option('value');
                      if (!actual || String(actual).trim() === '') {
                        const desde = frmRef.current?.instance.getEditor('OrdenDesde')?.option('value');
                        if (desde && String(desde).trim() !== '') {
                          e.component.option('value', desde);
                        }
                      }
                    },
                  }}>
                  <Label text='Orden hasta' />
                </FormItem>
                <FormItem dataField='Paciente' colSpan={2}>
                  <Label text='Documento' />
                </FormItem>
                <FormItem dataField='Areas' colSpan={2} editorOptions={{ items: LstAreas, showSelectionControls: true, showClearButton: true, showDataBeforeSearch: true, minSearchLength: 2, searchTimeout: 200, searchEnabled: true, displayExpr: 'Aa_Nombre', valueExpr: 'Aa_Id' }} editorType='dxTagBox'>
                  <Label text='Área' />
                </FormItem>
                <FormItem
                  dataField='Estudios'
                  colSpan={2}
                  cssClass='full-width-item'
                  render={() => (
                    <DropDownBox
                      value={filterStudiesDropDown.studiesDropDownValue}
                      opened={filterStudiesDropDown.isStudiesDropDownOpened}
                      valueExpr='Es_Id'
                      deferRendering={false}
                      displayExpr={filterStudiesDropDown.studiesDisplayExpr}
                      placeholder='Seleccionar estudio...'
                      showClearButton={true}
                      stylingMode='outlined'
                      className='comboboxItems'
                      focusStateEnabled={true}
                      width='100%'
                      ref={refDropDownStudies}
                      dataSource={filterStudiesDropDown.studiesDataSource}
                      onValueChanged={filterStudiesDropDown.syncStudiesSelection}
                      onOptionChanged={filterStudiesDropDown.onStudiesDropDownOpened}
                      onKeyDown={filterStudiesDropDown.onStudiesKeyDown}
                      contentRender={() => (
                        <FilterStudiesDataGrid
                          studiesDataSource={filterStudiesDropDown.studiesDataSource}
                          studiesDropDownValue={filterStudiesDropDown.studiesDropDownValue}
                          onStudiesSelectionChanged={filterStudiesDropDown.onStudiesSelectionChanged}
                          onStudiesRowDblClick={filterStudiesDropDown.onStudiesRowDblClick}
                          studiesSearchValue={filterStudiesDropDown.studiesSearchValue}
                          onStudiesSearchValueChanged={filterStudiesDropDown.onStudiesSearchValueChanged}
                          onStudiesKeyDown={filterStudiesDropDown.onStudiesKeyDown}
                          setIsStudiesDropDownOpened={filterStudiesDropDown.setIsStudiesDropDownOpened}
                          refDropDownStudies={refDropDownStudies}
                        />
                      )}
                      dropDownOptions={{
                        width: isXSmall ? '100vw' : 700,
                        height: isXSmall ? '100vh' : 500,
                        showTitle: false,
                        shading: false,
                        closeOnOutsideClick: !isXSmall,
                      }}
                    />
                  )}>
                  <Label text='Estudio' />
                </FormItem>
                <FormItem dataField='Servicios' colSpan={2} visible={LstServicios && LstServicios.length > 0} editorOptions={{ items: LstServicios, showSelectionControls: true, displayExpr: 'Se_Nombre', valueExpr: 'Se_Id' }} editorType='dxTagBox'>
                  <Label text='Servicio' />
                </FormItem>
                <FormItem dataField='Sedes' colSpan={2} visible={LstSedes && LstSedes.length > 0 && ModuleLocation && Prm_Ord_ViewOtherLocation} editorOptions={{ items: LstSedes, showSelectionControls: true, displayExpr: 'Sd_Nombre', valueExpr: 'Sd_Id' }} editorType='dxTagBox'>
                  <Label text='Sede' />
                </FormItem>
                <FormItem
                  dataField='Remitido'
                  colSpan={1}
                  editorType='dxSelectBox'
                  editorOptions={{
                    items: [
                      { text: 'Todas', value: undefined },
                      { text: 'Solo remitidas', value: true },
                      { text: 'No remitidas', value: false },
                    ],
                    displayExpr: 'text',
                    valueExpr: 'value',
                    showClearButton: true,
                    stylingMode: 'outlined',
                  }}>
                  <Label text='Remitido' />
                </FormItem>

                {/* Campos dinámicos de órdenes */}
                {dynamicOrderFields &&
                  dynamicOrderFields.map((field: SystemFields) => {
                    const currentDep = dynamicOrderFields.find((y) => field.Dfc_DependencyDefinition === y.Dfc_Id)?.Dfc_Equivalencia;
                    return <FormItem key={field.Dfc_Id} colSpan={1} render={() => <CustomFormControl valueDepency={field.Dfc_Dependency && currentDep ? dynamicFieldValues[currentDep] : undefined} value={dynamicFieldValues[field.Dfc_Equivalencia] || null} data={field} onValueChange={handleDynamicFieldChange(field.Dfc_Equivalencia)} isEditing={true} />} />;
                  })}
              </Form>

              <button type='submit' style={{ display: 'none' }} aria-hidden />
            </form>
          </ScrollView>
          <ToolbarItem toolbar='bottom' location='before'>
            <Button text='Limpiar' stylingMode='text' icon='clear' onClick={handleClearFilters} />
          </ToolbarItem>
          <ToolbarItem toolbar='bottom' location='after'>
            <div className='form-popup-buttons-container'>
              <Button text='Cancelar' stylingMode='contained' onClick={handleCancel} />
              <Button text='Confirmar' disabled={loading} stylingMode='contained' type='default' onClick={handleConfirm} />
            </div>
          </ToolbarItem>
        </Popup>
      </div>

      {modalPrintResult.visible && <ModalReportResults visible={modalPrintResult.visible} setVisible={setModalPrintResult} reportName='Resultados' idOrd={-1} orders={modalPrintResult.orders} studies={modalPrintResult.studies} areas={modalPrintResult.areas} />}

      {modalSendMail.visible && modalSendMail.dataOrder && <SendMailMasive visible={modalSendMail.visible} setVisible={setModalSendMail} dataOrder={modalSendMail.dataOrder} studies={modalSendMail.studies} areas={modalSendMail.areas} />}

      <RightPanelControlled isVisible={isPanelOpen} isPinned={false} onClose={handleClosePanel} includePin={false} title='Consulta de Orden'>
        {loadingPanel ? (
          <div style={{ padding: '30px', textAlign: 'center' }}>
            <LoadIndicator visible={true} />
            <div style={{ marginTop: '15px', color: '#666' }}>Cargando información...</div>
          </div>
        ) : (
          <>
            <OrderInfo orderData={currentOrder} />
            <PatientInfo patientData={currentPatient} />
            <div style={{ padding: '15px', borderTop: '1px solid #e0e0e0' }}>
              <Button text='IR A LA ORDEN' type='default' stylingMode='contained' width='100%' onClick={handleClickViewOrder} disabled={!currentOrder || loadingFindOrder} />
            </div>
          </>
        )}
      </RightPanelControlled>

      <LoadPanel visible={loading} />
    </div>
  );
};

export default Index;
